import React from 'react';

import { classnames, useClassnames } from '@lumapps/classnames';
import { useFormContext } from '@lumapps/lumx-forms';
import { FormSubSection } from '@lumapps/lumx-forms/components/FormSubSection';
import { FormTextField } from '@lumapps/lumx-forms/components/FormTextField';
import { ColorPalette, Text, Typography } from '@lumapps/lumx/react';
import type { InputVariableDefinition } from '@lumapps/micro-apps-builder/types';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { MICRO_APP_PICKER } from '../../keys';
import './index.scss';

export interface InputVariablesSetupProps {
    inputVariablesDefinitions: InputVariableDefinition[];
    isDisplayed: boolean;

    onErrorChange: (hasError: boolean) => void;
}

const CLASSNAME = 'input-variables-setup';

/**
 *
 * @param InputVariablesSetupProps
 * @returns InputVariablesSetup
 */
export const InputVariablesSetup: React.FC<InputVariablesSetupProps> = ({
    inputVariablesDefinitions,
    isDisplayed,
    onErrorChange,
}) => {
    const { translateKey } = useTranslate();
    const { element, block } = useClassnames(CLASSNAME);
    const form = useFormContext();
    const { formState } = form;

    const mandatoryInputs = inputVariablesDefinitions.filter((input) => input.required);
    const optionalInputs = inputVariablesDefinitions.filter((input) => !input.required);

    const hasErrors = inputVariablesDefinitions.some((input) => !!formState.errors[input.id]);

    React.useEffect(() => {
        onErrorChange(hasErrors);
    }, [hasErrors, onErrorChange]);

    return (
        /**
         * React-hook-form is not really convenient when a form is scattered across multiple tabs that can dismount.
         * The problem is if this component is unmounted, we lost the validation on every input in this component.
         * To avoid that, I simply hide the component when not selected tab wise.
         */
        <section className={classnames(block(), isDisplayed ? '' : element('is_hidden'))}>
            <Text as="span" typography={Typography.body1} color={ColorPalette.grey}>
                {translateKey(MICRO_APP_PICKER.INPUT_VARIABLES_DESCRIPTION)}
            </Text>

            {mandatoryInputs.length > 0 && (
                <FormSubSection
                    className={element('mandatory')}
                    title={translateKey(GLOBAL.MANDATORY)}
                    titleProps={{ as: 'h5' }}
                    withSeparator={false}
                >
                    {mandatoryInputs.map((input) => (
                        <FormTextField
                            helper={input.helper?.value}
                            id={input.id}
                            key={input.id}
                            label={input.title?.value || input.id}
                            name={input.id}
                            isRequired
                            clearButtonProps
                        />
                    ))}
                </FormSubSection>
            )}

            {optionalInputs.length > 0 && (
                <FormSubSection
                    className={element('optional')}
                    title={translateKey(GLOBAL.OPTIONAL)}
                    titleProps={{ as: 'h5' }}
                    withSeparator={false}
                >
                    {optionalInputs.map((input) => (
                        <FormTextField
                            helper={input.helper?.value}
                            id={input.id}
                            key={input.id}
                            label={input.title?.value || input.id}
                            name={input.id}
                            clearButtonProps
                        />
                    ))}
                </FormSubSection>
            )}
        </section>
    );
};
